import { FilterRangeEndDatePicker, FilterRangeStartDatePicker } from "components/DatePicker/RangeDatePicker";
import { FilterDatePicker } from "components/DatePicker/SingleDatePicker";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";
import moment, { type Moment } from "moment";
import React, { useState } from "react";
import { type GroupBase } from "react-select";
import { getMonthsList, getQuartersList, getYearsList, type PeriodOption } from "./timeutils";

interface IProps {
  periodFilter: server.dto.PeriodFilterOptions
  onChange: (periodStart: Moment, periodEnd: Moment, closeFilter: boolean) => void

  initialStart: moment.Moment
  initialEnd: moment.Moment
}

interface ISubPeriodProps {
  onChange: (periodStart: Moment, periodEnd: Moment, closeFilter: boolean) => void

  initialStart: moment.Moment
  initialEnd: moment.Moment
}

const zeroMoment = moment.utc(0);

interface PeriodDropdownProps {
  options: Array<GroupBase<PeriodOption>>
  initialValue: number
  onChange: (value: number) => void
}

const PeriodDropdown = (props: PeriodDropdownProps) => {
  const [value, setValue] = useState(props.initialValue);

  return <BaseReactSelectDropdown<PeriodOption>
    excludeKeys={undefined}
    options={props.options}
    getLabel={o => o.label}
    getValue={o => o.value}
    value={props.options.flatMap(g => g.options).find(o => o.value === value)}
    onChange={o => {
      setValue(o.value);
      props.onChange(o.value);
    }}
    config={{ isSearchable: true }}
  />;
};

const PerYear = (props: ISubPeriodProps) => {
  const [year, setYear] = useState(moment.utc(props.initialStart).year());

  const onChange = (selectedYear: number, closeFilter: boolean) => {
    setYear(selectedYear);

    const start = moment.utc(selectedYear, "YYYY").startOf("year");
    const end = start.clone().add(1, "year");

    props.onChange(start, end, closeFilter);
  };

  const years = getYearsList().map(y => ({ value: y }));

  return <BaseReactSelectDropdown
    excludeKeys={[]}
    options={years}
    getLabel={y => y.value.toString()}
    getValue={y => y.value}
    value={{ value: year }}
    onChange={y => { onChange(y.value, true); }}
    config={{ isSearchable: true }}
  />;
};

export const PerQuarter = (props: ISubPeriodProps) => {
  const onChange = (value: number, closeFilter: boolean) => {
    const start = moment.utc(value);
    const end = start.clone().add(3, "months");

    props.onChange(start, end, closeFilter);
  };

  const quarters = getQuartersList().map(({ year, entries }) => ({ label: year.toString(), options: entries }));
  const defaultValue = moment.utc(props.initialStart).startOf("quarter").valueOf();

  return <PeriodDropdown
    options={quarters}
    initialValue={defaultValue}
    onChange={value => { onChange(value, true); }}
  />;
};

export const PerMonth = (props: ISubPeriodProps) => {
  const onChange = (value: number, closeFilter: boolean) => {
    if (value === 0) {
      props.onChange(zeroMoment, zeroMoment, closeFilter);
      return;
    }

    const start = moment.utc(value);
    const end = start.clone().add(1, "month");

    props.onChange(start, end, closeFilter);
  };

  const months = getMonthsList().map(({ year, entries }) => ({ label: year.toString(), options: entries }));
  const defaultValue = moment.utc(props.initialStart).startOf("month").valueOf();

  return <PeriodDropdown
    options={months}
    initialValue={defaultValue}
    onChange={value => { onChange(value, true); }}
  />;
};

export const PerWeek = (props: ISubPeriodProps) => {
  const weekOptions = Object.keys(serverReference.yearsWithWeeks)
    .map(year => ({
      label: year,
      options: (serverReference.yearsWithWeeks[year] as any as number[])
        .map((time, idx) => ({ label: `${RESX.DateTime.Week} ${idx + 1} ${year}`, value: time }))
    }));

  const onChange = (value: number, closeFilter: boolean) => {
    if (value === 0) {
      props.onChange(zeroMoment, zeroMoment, closeFilter);
      return;
    }

    const start = moment.utc(value);
    const end = start.clone().add(1, "week");

    props.onChange(start, end, closeFilter);
  };

  const defaultValue = moment.utc(props.initialStart).startOf("week").valueOf();

  return <PeriodDropdown
    options={weekOptions}
    initialValue={defaultValue}
    onChange={value => { onChange(value, true); }}
  />;
};

const PerDay = (props: ISubPeriodProps) => {
  const [date, setDate] = useState<Moment>(moment.utc(props.initialStart).startOf("day"));

  const onChange = (newDate: Moment, closeFilter: boolean) => {
    setDate(newDate);

    const beginMoment = newDate.clone().startOf("day");
    const endMoment = newDate.clone().endOf("day");

    props.onChange(beginMoment, endMoment, closeFilter);
  };

  return <FilterDatePicker
    minDate={moment.utc(serverReference.constants.appointment.minDate)}
    maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
    onChange={(date, fromCalendar) => { onChange(date, fromCalendar); }}
    value={date}
  />;
};

const From = (props: ISubPeriodProps) => {
  const [date, setDate] = useState<Moment>(moment.utc(props.initialStart).startOf("day"));

  const onChange = (newDate: Moment, closeFilter: boolean) => {
    setDate(newDate);

    const beginMoment = newDate.clone().startOf("day");
    const endMoment = moment.utc(props.initialEnd);

    props.onChange(beginMoment, endMoment, closeFilter);
  };

  return <FilterDatePicker
    minDate={moment.utc(serverReference.constants.appointment.minDate)}
    maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
    onChange={(d, fromCalendar) => { onChange(d, fromCalendar); }}
    value={date}
  />;
};

const UntilAndIncluding = (props: ISubPeriodProps) => {
  const [date, setDate] = useState<Moment>(moment.utc(props.initialEnd).endOf("day"));
  const onChange = (newDate: Moment, closeFilter: boolean) => {
    setDate(newDate);
    const beginMoment = moment(props.initialStart);
    const endMoment = newDate.clone().endOf("day");

    props.onChange(beginMoment, endMoment, closeFilter);
  };

  return <FilterDatePicker
    minDate={moment.utc(serverReference.constants.appointment.minDate)}
    maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
    value={date}
    onChange={(d, fromCalendar) => { onChange(d, fromCalendar); }}
  />;
};

const FromUntilAndIncluding = (props: ISubPeriodProps) => {
  const [beginMoment, setBeginMoment] = useState<Moment>(moment.utc(props.initialStart).clone().startOf("day"));
  const [endMoment, setEndMoment] = useState<Moment>(props.initialEnd ? moment.utc(props.initialEnd) : beginMoment.clone().add(1, "day").endOf("day"));

  const onBeginChange = (newDate: Moment) => {
    const begin = newDate.clone().startOf("day");
    let newEndMoment = endMoment;
    if (begin.isAfter(endMoment)) {
      newEndMoment = begin.clone().endOf("day");
      setEndMoment(newEndMoment);
    }
    setBeginMoment(begin);
    props.onChange(begin, newEndMoment, false);
  };

  const onEndChange = (newDate: Moment) => {
    const end = newDate.clone().endOf("day");
    let newBeginMoment = beginMoment;
    if (end.isBefore(beginMoment)) {
      newBeginMoment = end.clone().startOf("day");
      setBeginMoment(newBeginMoment);
    }
    setEndMoment(end);
    props.onChange(newBeginMoment, end, false);
  };

  return <div className="flex">
    <FilterRangeStartDatePicker
      minDate={moment.utc(serverReference.constants.appointment.minDate)}
      maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
      value={beginMoment}
      rangeEnd={endMoment}
      onChange={onBeginChange}
    />

    <FilterRangeEndDatePicker
      dontUpdateOnInputChange
      minDate={moment.utc(serverReference.constants.appointment.minDate)}
      maxDate={moment.utc(serverReference.constants.appointment.maxDate)}
      value={endMoment}
      rangeStart={beginMoment}
      onChange={onEndChange}
    />
  </div>;
};

const PeriodFilterSubPeriod = (props: IProps) => {
  switch (props.periodFilter) {
    case "perYear": return <PerYear onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perQuarter": return <PerQuarter onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perMonth": return <PerMonth onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perWeek": return <PerWeek onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "perDay": return <PerDay onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "from": return <From onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "untilAndIncluding": return <UntilAndIncluding onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    case "fromUntilAndIncluding": return <FromUntilAndIncluding onChange={props.onChange} initialStart={props.initialStart} initialEnd={props.initialEnd} />;
    default: throw new Error("Unrecognized period filter: " + props.periodFilter);
  }
};

export default PeriodFilterSubPeriod;